/**
 * Tokens del sistema de diseño de la plataforma ("Modernist"), según el handoff de diseño
 * del panel de administración.
 *
 * Reglas que no son negociables porque sostienen el white-label:
 *  - El **rojo del sistema** (`systemRed`) es el único que comunica estado: en vivo, error,
 *    atención. No es configurable por cliente.
 *  - El **color del cliente** NO está acá: llega del backend (`Tenant.primaryColor` y
 *    `secondaryColor`) y las apps lo aplican por custom property. Identifica y marca la
 *    interacción; **nunca** comunica estado.
 *  - `primary` es tinta y `secondary` neutral: es lo que se ve cuando un medio todavía no cargó
 *    sus colores, y lo que usa el backoffice, que no es de ningún medio.
 *  - **Radio 0** en todo. La separación es por reglas, no por sombras.
 */
/** Roles base. */
export const palette = {
    /** Ground de la app. */
    bg: '#F3F2F2',
    /** Barras y paneles. */
    surface: '#EAE9E9',
    /** Inputs y tarjetas. */
    field: '#FFFFFF',
    /** Texto y acción primaria. */
    ink: '#201E1D',
    /** En vivo · error · atención · foco. */
    systemRed: '#EC3013',
};
/** Rampa neutral 100→900 (misma escala perceptual que la roja). */
export const neutral = {
    100: '#F8F4F4',
    200: '#EAE7E7',
    300: '#D7D3D3',
    400: '#BAB6B6',
    500: '#9B9797',
    600: '#7D7979',
    700: '#605D5D',
    800: '#444141',
    900: '#2D2B2B',
};
/**
 * Rampa roja 100→900. Para **texto de párrafo en rojo usar siempre el 700** (`#AE1800`,
 * 7,1:1). El rojo base es 3,4:1: sirve para reglas, puntos de estado, íconos y texto ≥20 px.
 */
export const red = {
    100: '#FFF2EF',
    200: '#FFE0D9',
    300: '#FFC4B8',
    400: '#FF9783',
    500: '#FF563C',
    600: '#DD2B0F',
    700: '#AE1800',
    800: '#7C1405',
    900: '#4D170E',
};
/**
 * Roles con la forma que espera MUI. Son el respaldo del white-label: `primary` en tinta y
 * `secondary` en neutral es lo que se ve mientras el medio no tenga sus colores cargados (ver
 * arriba). El panel los pisa por variable, no acá.
 */
export const colors = {
    primary: {
        main: palette.ink,
        light: neutral[800],
        dark: '#000000',
        contrastText: '#FFFFFF',
    },
    secondary: {
        main: neutral[700],
        light: neutral[500],
        dark: neutral[900],
        contrastText: '#FFFFFF',
    },
    error: { main: palette.systemRed, light: red[400], dark: red[700], contrastText: '#FFFFFF' },
    warning: { main: red[600], light: red[300], dark: red[800], contrastText: '#FFFFFF' },
    info: { main: neutral[800], light: neutral[600], dark: neutral[900], contrastText: '#FFFFFF' },
    success: { main: '#1F6F3F', light: '#3E9160', dark: '#144A29', contrastText: '#FFFFFF' },
    /** Alias de la rampa neutral con las claves de MUI. */
    grey: {
        50: neutral[100],
        100: neutral[100],
        200: neutral[200],
        300: neutral[300],
        400: neutral[400],
        500: neutral[500],
        600: neutral[600],
        700: neutral[700],
        800: neutral[800],
        900: neutral[900],
    },
};
/** Base 8. Escala en uso: 4 · 8 · 12 · 16 · 24 · 32. */
export const spacing = (n) => `${n * 8}px`;
/** Radio 0 en todo el sistema: botones, campos, chips, diálogos, avatares. */
export const radius = { sm: 0, md: 0, lg: 0, pill: 0 };
export const typography = {
    fontFamily: '"Archivo", system-ui, -apple-system, sans-serif',
    weight: { regular: 400, medium: 600, bold: 800 },
    /** Interletrado de titulares. */
    headingLetterSpacing: '-0.015em',
    /** Tamaño / línea por variante, según la tabla del handoff. */
    scale: {
        h1: { size: 34, line: 38 },
        h2: { size: 30, line: 34 },
        h3: { size: 25, line: 28 },
        h4: { size: 20, line: 24 },
        h5: { size: 16, line: 20 },
        h6: { size: 13, line: 16 },
        subtitle1: { size: 17, line: 25 },
        body1: { size: 15, line: 23 },
        body2: { size: 13, line: 19 },
        button: { size: 14, line: 17 },
        caption: { size: 11, line: 15 },
        /** Superficie de escritura del editor de notas. */
        editorBody: { size: 17, line: 27 },
    },
};
/**
 * Sombras: solo tres usos. Menú y snackbar (`sm`), fila arrastrada (`md`), diálogo y hoja
 * lateral (`lg`). Nada más lleva sombra.
 */
export const shadows = {
    sm: '0 1px 2px rgba(45,43,43,.14)',
    md: '0 3px 10px rgba(45,43,43,.16)',
    lg: '0 12px 32px rgba(45,43,43,.22)',
};
/** Bordes estructurales. */
export const borders = {
    /** Campos. */
    field: `1px solid ${neutral[500]}`,
    /** Divisiones estructurales. */
    structural: `2px solid ${palette.ink}`,
    /** Regla entre filas de un listado. */
    row: `1px solid ${neutral[300]}`,
    /** Marca de destino al arrastrar. */
    drop: `4px solid ${palette.systemRed}`,
};
/** Alturas de control: 40 en escritorio, 48 en pantalla chica para todo lo que se toca. */
export const controlHeight = { desktop: 40, touch: 48 };
/** Anchos fijos del shell del panel. */
export const layout = { sidebarWidth: 236, editorSidePanel: 392 };
/** Foco de teclado: siempre este, nunca el del browser. */
export const focusRing = {
    outline: `2px solid ${palette.systemRed}`,
    outlineOffset: '2px',
};
